import { HARVEST_STRIP } from "../sprite/harvest-strip.ts";

/**
 * A character's animation bits (ADR-074 decision 7). For a player, and a creature from P2.4-T1, the six animation
 * bits of the snapshot's facing byte hold a 5-bit state and, above it, a restart bit the server flips each time it
 * writes a one-shot state, so a repeated swing or hurt still changes the byte. Things keep ADR-042's state index in
 * all six bits; the entity's kind decides the reading.
 */

/**
 * The states. Dead and cast are held while they hold; the others play once when the byte changes to them. Jump is a
 * picture and nothing else (ADR-116 decisions 17 and 19), the lowest rank of the tick's one state. The three drink
 * states play the `heal` strip like `Heal` does, and a page adds a potion's burst in the variant's tone (ADR-074's
 * amendment of 2026-10-27); `Heal` stays a spell's. Harvest is held while a harvest hold runs (ADR-142's amendment of
 * 2026-10-31) and a page loops its strip while it shows. 13 to 31 are reserved.
 */
export const CharacterState = { None: 0, Dead: 1, Hurt: 2, Attack1: 3, Attack2: 4, Dash: 5, Heal: 6, Cast: 7, Jump: 8, DrinkHealth: 9, DrinkStamina: 10, DrinkCure: 11, Harvest: 12 } as const;
export type CharacterState = (typeof CharacterState)[keyof typeof CharacterState];

/**
 * The restart bit among the six animation bits: bit 7 of the facing byte.
 */
export const RESTART_BIT = 0x20;

const STATE_MASK = 0x1f;

/**
 * The animation bits for a state and a restart bit.
 *
 * @param state The state, 0 to 31.
 * @param restart Whether the restart bit is set.
 * @returns The six animation bits.
 * @throws Error when the state does not fit five bits.
 */
export function characterAnim(state: number, restart: boolean): number {
  if (!Number.isInteger(state) || state < 0 || state > STATE_MASK) {
    throw new Error(`character state ${state} does not fit five bits`);
  }
  return restart ? state | RESTART_BIT : state;
}

/**
 * The state a character's animation bits carry, without the restart bit.
 *
 * @param anim The six animation bits.
 * @returns The state, 0 to 31.
 */
export function characterStateOf(anim: number): number {
  return anim & STATE_MASK;
}

/**
 * The action state each strip plays (ADR-074 decision 7): `attack1` and `attack2`, which Strike's combo alternates and
 * Heavy Strike's chain plays 1-2-1 (ADR-073's amendment "the Strike combo and the Heavy Strike chain"), `dash` and
 * `heal`. The rules validator refuses an ability, a combo or a chain whose strip has no state here.
 */
export const ACTION_STATES: ReadonlyMap<string, CharacterState> = new Map<string, CharacterState>([
  ["attack1", CharacterState.Attack1],
  ["attack2", CharacterState.Attack2],
  ["dash", CharacterState.Dash],
  ["heal", CharacterState.Heal],
]);

/**
 * The strip each state plays, by state: dead the death strip, hurt the hurt strip, each action the strip
 * `ACTION_STATES` names for it, jump the `jump` strip, which no ability names, each drink state the `heal` strip. None
 * is motion's, cast's pose is ADR D's, harvest the `harvest` strip, and 13 to 31 are reserved.
 */
const STATE_STRIPS: readonly (string | null)[] = [null, "death", "hurt", "attack1", "attack2", "dash", "heal", null, "jump", "heal", "heal", "heal", HARVEST_STRIP];

/**
 * The strip a state plays on the page (P2.1-T4).
 *
 * @param state The state, 0 to 31.
 * @returns The strip's name, or null for none, cast and a reserved state.
 */
export function stripOfState(state: number): string | null {
  return STATE_STRIPS[state] ?? null;
}

/**
 * The greatest state this build knows: a state above it is reserved, draws nothing new and is logged once.
 */
export const LAST_KNOWN_STATE: number = CharacterState.Harvest;

/**
 * The drink variants and the state each is written as (ADR-074's amendment of 2026-10-27): a potion's record names its
 * variant, the map server writes the state, and a page reads the variant back from the state.
 */
export const DRINK_STATES: ReadonlyMap<string, CharacterState> = new Map<string, CharacterState>([
  ["health", CharacterState.DrinkHealth],
  ["stamina", CharacterState.DrinkStamina],
  ["cure", CharacterState.DrinkCure],
]);

/**
 * The drink variant a state carries.
 *
 * @param state The state, 0 to 31.
 * @returns `health`, `stamina` or `cure` for a drink state, null for any other.
 * @example
 * drinkVariantOfState(CharacterState.DrinkCure);
 * // "cure"
 */
export function drinkVariantOfState(state: number): string | null {
  for (const [variant, drink] of DRINK_STATES) {
    if (drink === state) {
      return variant;
    }
  }
  return null;
}

/**
 * The state an ability's use writes: its drink variant's state for a potion's record, otherwise the state its strip
 * names.
 *
 * @param strip The ability's strip, one `ACTION_STATES` holds.
 * @param drink The ability's `drink`, null for none.
 * @returns The state.
 * @throws Error when the strip has no action state or the variant names no drink state.
 * @example
 * actionStateOf("heal", { variant: "cure" });
 * // CharacterState.DrinkCure
 */
export function actionStateOf(strip: string, drink: { readonly variant: string } | null): number {
  const state = drink === null ? ACTION_STATES.get(strip) : DRINK_STATES.get(drink.variant);
  if (state === undefined) {
    throw new Error(drink === null ? `strip ${strip} has no action state` : `drink variant ${drink.variant} has no state`);
  }
  return state;
}

